import { PolicyStatus } from "@/types";

/**
 * Maps each dashboard filter key to its Policy List filter/param key. The
 * dashboard uses plural junction keys (`tpa_vendor_ids`) while the list/backend
 * expects the singular form (`tpa_vendor_id`); client/carrier keys are identical.
 * All four are now honored by the list backend (`= ANY(...)`, see AB#4967).
 */
const CARRIED_FILTERS: ReadonlyArray<readonly [dashboardKey: string, listKey: string]> = [
  ["client_id_ref", "client_id_ref"],
  ["vendor_id_ref", "vendor_id_ref"],
  ["tpa_vendor_ids", "tpa_vendor_id"],
  ["rx_vendor_ids", "rx_vendor_id"],
];

/** True when a filter value carries at least one selection. */
const hasValue = (value: string | string[] | undefined): boolean => {
  if (value === undefined) return false;
  return Array.isArray(value) ? value.length > 0 : value !== "";
};

/** Extra filter entries a specific card contributes (e.g. a benefit-presence flag). */
type ExtraFilter = Record<string, string | number | boolean | string[]>;

/**
 * Builds a Policy List drill-through URL for a Policy Dashboard top card.
 *
 * Always forces `status: active` (the dashboard metrics are computed over active
 * policies) and carries over the dashboard's current Client / Carrier / TPA / Rx
 * filters — including multi-select arrays — so the destination list reflects the
 * same population the metric was computed over. The backend matches each carried
 * filter against ANY of the supplied values (`= ANY(...)`; see AB#4967).
 *
 * `extra` lets a card add its own predicate on top — e.g. the average cards pass
 * `{ has_specific_benefit: true }` / `{ has_aggregate_benefit: true }` so the
 * list shows exactly the policies that average was computed over.
 *
 * @param filters - The dashboard's currently-active filter object (parsed from
 *   the `filter` URL search param).
 * @param extra - Card-specific filter entries merged into the destination filter.
 * @returns A hash-router path like `/policies?filter={"status":"active",...}`.
 */
export const buildPolicyDrillLink = (filters: Record<string, string | string[]>, extra: ExtraFilter = {}): string => {
  const drillFilter: Record<string, string | number | boolean | string[]> = {
    status: PolicyStatus.ACTIVE,
    ...extra,
  };
  for (const [dashboardKey, listKey] of CARRIED_FILTERS) {
    const value = filters[dashboardKey];
    if (hasValue(value)) drillFilter[listKey] = value;
  }
  // Encode the filter — reserved chars in ids (#, &, +, space) would otherwise
  // corrupt the query string, and `#` is especially dangerous under HashRouter.
  // Matches the repo convention (ClientReportingTab, CarrierReportingTab, etc.).
  return `/policies?filter=${encodeURIComponent(JSON.stringify(drillFilter))}`;
};
